<template>
  <div class="level-models-page">
    <v-container fluid class="pa-0">
      <v-row class="ma-0 fill-height">
        <!-- Maturity Levels Card -->
        <v-col cols="6" class="pa-2">
          <v-card>
            <v-card-title class="d-flex align-center bg-grey-lighten-4">
              <v-icon size="20" class="mr-2">mdi-chart-bell-curve</v-icon>
              Maturity Levels
              <v-spacer></v-spacer>
              <v-btn
                size="x-small"
                variant="text"
                color="primary"
                class="mr-2"
                @click="showMaturityColorDialog = true"
              >
                <v-icon size="16" class="mr-1">mdi-palette</v-icon>
                Define Colors for the range 
              </v-btn>
              <v-btn
                size="x-small"
                variant="text"
                color="primary"
                @click="isAddingMaturityLevel = true"
              >
                <v-icon size="16">mdi-plus</v-icon>
                Add
              </v-btn>
            </v-card-title>
            <v-card-text class="pa-4">
              <!-- Grid -->
              <div 
                class="grid-container"
                :class="{ 'grid-empty': !maturityLevels.length }"
                :style="gridContainerStyle(maturityLevels.length)"
              >
                <MaturityLevelsGrid
                  v-if="maturityLevels.length > 0"
                  ref="maturityGrid"
                  :filtered="true"
                  :filteredItems="maturityLevels"
                  @grid-ready="onMaturityGridReady"
                  @cell-value-changed="handleMaturityCellValueChanged"
                  @update-success="handleUpdateSuccess"
                  @update-error="handleUpdateError"
                  @delete-level="confirmDeleteMaturityLevel"
                  class="grid-component"
                  style="height: 100%;"
                />
                <div v-else class="empty-state">
                  <v-icon size="40" color="grey-lighten-1">mdi-chart-bell-curve</v-icon>
                  <div class="empty-text">No maturity levels defined</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Confidence Levels Card -->
        <v-col cols="6" class="pa-2">
          <v-card>
            <v-card-title class="d-flex align-center bg-grey-lighten-4">
              <v-icon size="20" class="mr-2">mdi-alert-circle</v-icon>
              Confidence Levels
              <v-spacer></v-spacer>
              <v-btn
                size="x-small"
                variant="text"
                color="primary"
                class="mr-2"
                @click="showRiskColorDialog = true"
              >
                <v-icon size="16" class="mr-1">mdi-palette</v-icon>
                Define Colors for the range
              </v-btn>
              <v-btn
                size="x-small"
                variant="text"
                color="primary"
                @click="isAddingRiskLevel = true"
              >
                <v-icon size="16">mdi-plus</v-icon>
                Add
              </v-btn>
            </v-card-title>
            <v-card-text class="pa-4">
              <!-- Grid -->
              <div 
                class="grid-container"
                :class="{ 'grid-empty': !riskLevels.length }"
                :style="gridContainerStyle(riskLevels.length)"
              >
                <RiskLevelsGrid
                  v-if="riskLevels.length > 0"
                  ref="riskGrid"
                  :filtered="true"
                  :filteredItems="riskLevels"
                  @grid-ready="onRiskGridReady"
                  @cell-value-changed="handleRiskCellValueChanged"
                  @delete-level="confirmDeleteRiskLevel"
                  class="grid-component"
                  style="height: 100%;"
                />
                <div v-else class="empty-state">
                  <v-icon size="40" color="grey-lighten-1">mdi-alert-circle</v-icon>
                  <div class="empty-text">No confidence levels defined</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Maturity Color Dialog -->
    <v-dialog v-model="showMaturityColorDialog" width="auto" max-width="320">
      <v-card>
        <v-card-title class="text-h6 px-6 pt-4">Maturity Level Colors</v-card-title>
        <v-card-text class="px-6 pt-4 pb-0">
          <div class="mb-6">
            <div class="text-subtitle-1 mb-2">Start Color (Level 0)</div>
            <v-color-picker
              v-model="maturityStartColor"
              mode="rgb"
              width="auto"
              max-height="200"
            ></v-color-picker>
          </div>
          <div class="mb-6">
            <div class="text-subtitle-1 mb-2">End Color (Level 19)</div>
            <v-color-picker
              v-model="maturityEndColor"
              mode="rgb"
              width="auto"
              max-height="200"
            ></v-color-picker>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 py-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showMaturityColorDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="text" @click="applyMaturityColorGradient">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete {{ deleteTarget.type }} level
          <strong>{{ deleteTarget.level?.id ?? deleteTarget.level?.level }}</strong>
          ({{ deleteTarget.level?.name }})?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="text" @click="executeDelete" :loading="deleteLoading">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confidence Color Dialog -->
    <v-dialog v-model="showRiskColorDialog" max-width="320">
      <v-card>
        <v-card-title class="text-h6 px-6 pt-4">Confidence Level Colors</v-card-title>
        <v-card-text class="px-6 pt-4 pb-0">
          <div class="mb-6">
            <div class="text-subtitle-1 mb-2">Start Color (Level 0)</div>
            <v-color-picker
              v-model="riskStartColor"
              mode="rgb"
              width="auto"
              max-height="200"
            ></v-color-picker>
          </div>
          <div class="mb-6">
            <div class="text-subtitle-1 mb-2">End Color (Level 5)</div>
            <v-color-picker
              v-model="riskEndColor"
              mode="rgb"
              width="auto"
              max-height="200"
            ></v-color-picker>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 py-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showRiskColorDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="text" @click="applyRiskColorGradient">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { useStore } from 'vuex'
import { mapState, mapActions } from 'vuex'
import MaturityLevelsGrid from '@/components/data-grid/MaturityLevelsGrid.vue'
import RiskLevelsGrid from '@/components/data-grid/RiskLevelsGrid.vue'

export default defineComponent({
  name: 'LevelModelsPage',
  components: {
    MaturityLevelsGrid,
    RiskLevelsGrid
  },
  setup() {
    const store = useStore()
    return { store }
  },
  data() {
    return {
      isAddingMaturityLevel: false,
      isAddingRiskLevel: false,
      showMaturityColorDialog: false,
      showRiskColorDialog: false,
      maturityStartColor: '#FF0000',
      maturityEndColor: '#00FF00',
      riskStartColor: '#00FF00',
      riskEndColor: '#FF0000',
      showDeleteDialog: false,
      deleteLoading: false,
      deleteTarget: { type: '', level: null }
    }
  },
  computed: {
    ...mapState('levels', {
      maturityLevels: state => state.maturityLevels || [],
      maturityLevelsLoading: state => state.loading.maturityLevels,
      maturityLevelsError: state => state.error.maturityLevels,
      riskLevels: state => state.riskLevels || [],
      riskLevelsLoading: state => state.loading.riskLevels,
      riskLevelsError: state => state.error.riskLevels
    })
  },
  methods: {
    ...mapActions('levels', [
      'fetchMaturityLevels',
      'fetchRiskLevels',
      'updateMaturityLevel',
      'updateRiskLevel',
      'deleteMaturityLevel',
      'deleteRiskLevel'
    ]),
    gridContainerStyle(itemCount) {
      if (itemCount === 0) {
        return { 
          height: '80px',
          minHeight: '80px'
        };
      }

      return {
        height: '100%',
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column'
      };
    },
    onMaturityGridReady(params) {
      console.log('LevelModelsPage: Maturity grid ready', params);
      // Additional initialization if needed
    },
    onRiskGridReady(params) {
      console.log('LevelModelsPage: Risk grid ready', params);
      // Additional initialization if needed
    },
    async handleMaturityCellValueChanged(event) {
      try {
        await this.updateMaturityLevel({
          id: event.data.id,
          [event.column.colId]: event.newValue
        });
      } catch (error) {
        console.error('Failed to update maturity level:', error);
      }
    },
    async handleRiskCellValueChanged(event) {
      try {
        await this.updateRiskLevel({
          id: event.data.id,
          [event.column.colId]: event.newValue
        });
      } catch (error) {
        console.error('Failed to update risk level:', error);
      }
    },
    handleUpdateSuccess(event) {
      console.log('Update successful:', event);
      // Add success notification if needed
    },
    handleUpdateError(event) {
      console.error('Update failed:', event);
      // Add error notification if needed
    },
    interpolateColor(color1, color2, factor) {
      const hex = (x) => {
        const i = Math.round(x);
        return (i < 16 ? '0' : '') + i.toString(16);
      };

      const r1 = parseInt(color1.slice(1, 3), 16);
      const g1 = parseInt(color1.slice(3, 5), 16);
      const b1 = parseInt(color1.slice(5, 7), 16);

      const r2 = parseInt(color2.slice(1, 3), 16);
      const g2 = parseInt(color2.slice(3, 5), 16);
      const b2 = parseInt(color2.slice(5, 7), 16);

      const r = r1 + factor * (r2 - r1);
      const g = g1 + factor * (g2 - g1);
      const b = b1 + factor * (b2 - b1);

      return '#' + hex(r) + hex(g) + hex(b);
    },
    async applyMaturityColorGradient() {
      const levels = this.maturityLevels;
      const maxLevel = levels.length - 1;

      for (const level of levels) {
        const factor = level.id / maxLevel;
        const color = this.interpolateColor(this.maturityStartColor, this.maturityEndColor, factor);
        
        try {
          await this.updateMaturityLevel({
            id: level.id,
            color: color
          });
        } catch (error) {
          console.error('Failed to update maturity level color:', error);
        }
      }

      this.showMaturityColorDialog = false;
    },
    async applyRiskColorGradient() {
      const levels = this.riskLevels;
      const maxLevel = levels.length - 1;

      for (const level of levels) {
        const factor = level.id / maxLevel;
        const color = this.interpolateColor(this.riskStartColor, this.riskEndColor, factor);
        
        try {
          await this.updateRiskLevel({
            id: level.id,
            color: color
          });
        } catch (error) {
          console.error('Failed to update risk level color:', error);
        }
      }

      this.showRiskColorDialog = false;
    },
    confirmDeleteMaturityLevel(level) {
      this.deleteTarget = { type: 'maturity', level }
      this.showDeleteDialog = true
    },
    confirmDeleteRiskLevel(level) {
      this.deleteTarget = { type: 'confidence', level }
      this.showDeleteDialog = true
    },
    async executeDelete() {
      this.deleteLoading = true
      try {
        const id = this.deleteTarget.level?.id ?? this.deleteTarget.level?.level
        if (this.deleteTarget.type === 'maturity') {
          await this.deleteMaturityLevel({ apiService: this.$api, id })
        } else {
          await this.deleteRiskLevel({ apiService: this.$api, id })
        }
        this.showDeleteDialog = false
      } catch (error) {
        console.error('Failed to delete level:', error)
      } finally {
        this.deleteLoading = false
      }
    },
    async loadData() {
      try {
        await Promise.all([
          this.fetchMaturityLevels({ apiService: this.$api }),
          this.fetchRiskLevels({ apiService: this.$api })
        ])
      } catch (error) {
        console.error('Error loading data:', error)
      }
    }
  },
  async created() {
    console.log('LevelModelsPage: Component created');
    await this.loadData();
    console.log('LevelModelsPage: Data fetched', {
      maturityLevels: this.maturityLevels,
      riskLevels: this.riskLevels
    });
  }
})
</script>

<style scoped>
.level-models-page {
  height: 100%;
  width: 100%;
  display: flex;
}

.v-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.v-row {
  width: 100%;
  margin: 0;
  flex: 1;
}

.v-col {
  display: flex;
  padding: 8px;
}

.v-card {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.v-card-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 8px;
}

.grid-container {
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: var(--v-theme-surface);
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid var(--v-border-color);
  flex: 1;
  min-height: 0;
}

.grid-empty {
  border: 1px dashed var(--v-border-color);
  background-color: var(--v-theme-surface);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: var(--v-text-disabled);
}

.empty-text {
  margin-top: 0.5rem;
  font-size: 0.875rem;
}

.grid-component {
  flex: 1;
  min-height: 0;
  height: 100%;
}

.color-preset {
  cursor: pointer;
  min-width: 100px;
  justify-content: center;
}

/* AG Grid theme customization */
:deep(.ag-theme-balham) {
  --ag-row-height: 32px;
  --ag-header-height: 32px;
  --ag-grid-size: 4px;
  --ag-list-item-height: 32px;
  height: 100%;
}

:deep(.ag-root-wrapper) {
  border: none;
  height: 100%;
}

:deep(.ag-root) {
  border: none;
  height: 100%;
}

:deep(.ag-body-viewport) {
  min-height: 0;
}

:deep(.ag-center-cols-clipper) {
  min-height: 0;
}
</style> 